import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import TheatersIcon from '@mui/icons-material/Theaters';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

export const FOLDER_COLOR = '#4b6e7a';

// Purposes a folder can be tagged with (mirrors FolderPurposeEnum in the
// backend). Documents of that type are automatically filed into the folder.
export const FOLDER_PURPOSES = [
  { key: 'invoice', label: 'Invoices', Icon: ReceiptLongIcon },
  { key: 'offer', label: 'Offers', Icon: LocalOfferIcon },
  { key: 'order', label: 'Orders', Icon: ShoppingCartIcon },
  { key: 'contract', label: 'Contracts', Icon: HistoryEduIcon },
  { key: 'letter', label: 'Letters', Icon: MarkunreadMailboxIcon },
  { key: 'deliveryNote', label: 'Delivery Notes', Icon: LocalShippingIcon },
  { key: 'email', label: 'Emails', Icon: EmailIcon },
];

export function folderPurposes(entry) {
  const keys = entry?.meta?.purposes || [];
  return FOLDER_PURPOSES.filter((purpose) => keys.includes(purpose.key));
}

export function joinPath(dir, name) {
  return dir ? `${dir}/${name}` : name;
}

// SFM ("Services for Mac") mapping: SMB stores characters that are illegal
// in Windows file names as Unicode private-use codepoints - a folder created
// as "Vacation / Sick" on a Mac arrives here with U+F022 instead of the
// slashes. Decoded for display only; paths sent to the API keep the raw form.
const SFM_CHARACTERS = {
  '\uf020': '"',
  '\uf021': '*',
  '\uf022': '/',
  '\uf023': '<',
  '\uf024': '>',
  '\uf025': '?',
  '\uf026': '\\',
  '\uf027': '|',
  '\uf028': ' ',
  '\uf029': '.',
};

export function decodeSfmName(name) {
  return (name || '').replace(
    /[\uf020-\uf029]/g,
    (character) => SFM_CHARACTERS[character] || character,
  );
}

// Name shown for a listing entry. The backend sends displayName whenever the
// raw name contains encoded characters; decodeSfmName covers entries built
// client-side (breadcrumbs, cached listings).
export function entryLabel(entry) {
  return entry?.displayName || decodeSfmName(entry?.name);
}

// eslint-disable-next-line no-control-regex
const NAME_ILLEGAL_CHARACTERS = /[\\/<>:"|?*\u0000-\u001f]/;
const WINDOWS_RESERVED_NAMES = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i;

// Mirrors FileExplorerService::sanitizeName in the backend: a name typed in
// the explorer must be representable on Windows, macOS and Linux storage
// alike. Returns a translated error message, or null when the name is fine.
export function entryNameError(name, t) {
  const trimmed = (name || '').trim();
  if (!trimmed || trimmed === '.' || trimmed === '..')
    return t('Please enter a name');
  if (NAME_ILLEGAL_CHARACTERS.test(trimmed))
    return `${t('The name must not contain any of these characters')}: / \\ < > : " | ? *`;
  if (trimmed.endsWith('.')) return t('The name must not end with a dot');
  if (WINDOWS_RESERVED_NAMES.test(trimmed))
    return t('This name is not allowed on Windows storage');
  return null;
}

export function parentPath(path) {
  const index = path.lastIndexOf('/');
  return index === -1 ? '' : path.slice(0, index);
}

// Prefix paths of "a/b/c" -> ["", "a", "a/b", "a/b/c"]
export function pathPrefixes(path) {
  const prefixes = [''];
  if (!path) return prefixes;
  const segments = path.split('/');
  segments.forEach((_, index) =>
    prefixes.push(segments.slice(0, index + 1).join('/')),
  );
  return prefixes;
}

const EXTENSION_KINDS = [
  {
    extensions: ['pdf'],
    Icon: PictureAsPdfIcon,
    color: '#ab4141',
    label: 'PDF document',
  },
  {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic', 'tiff'],
    Icon: ImageIcon,
    color: '#3273bc',
    label: 'Image',
  },
  {
    extensions: ['doc', 'docx', 'rtf', 'odt'],
    Icon: DescriptionIcon,
    color: '#2B579A',
    label: 'Word document',
  },
  {
    extensions: ['xls', 'xlsx', 'csv', 'ods'],
    Icon: TableChartIcon,
    color: '#217346',
    label: 'Spreadsheet',
  },
  {
    extensions: ['ppt', 'pptx', 'odp'],
    Icon: SlideshowIcon,
    color: '#D24726',
    label: 'Presentation',
  },
  {
    extensions: ['txt', 'md', 'log'],
    Icon: TextSnippetIcon,
    color: '#7F8C8D',
    label: 'Text document',
  },
  {
    extensions: ['dwg', 'dxf'],
    Icon: ArchitectureIcon,
    color: '#dea300',
    label: 'Drawing',
  },
  {
    extensions: ['zip', 'rar', '7z'],
    Icon: FolderZipIcon,
    color: '#8a6d3b',
    label: 'Archive',
  },
  {
    extensions: ['eml', 'msg'],
    Icon: EmailIcon,
    color: '#3a6ea5',
    label: 'Email message',
  },
  {
    extensions: ['mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'],
    Icon: TheatersIcon,
    color: '#7a4b6e',
    label: 'Video',
  },
  {
    extensions: ['mp3', 'wav', 'ogg', 'm4a'],
    Icon: AudiotrackIcon,
    color: '#7a4b6e',
    label: 'Audio',
  },
];

const MIME_KINDS = {
  image: { Icon: ImageIcon, color: '#3273bc', label: 'Image' },
  video: { Icon: TheatersIcon, color: '#7a4b6e', label: 'Video' },
  audio: { Icon: AudiotrackIcon, color: '#7a4b6e', label: 'Audio' },
  text: { Icon: TextSnippetIcon, color: '#7F8C8D', label: 'Text document' },
};

// Kind (icon, color, translatable label) of a listing entry. Folders have no
// mimeType, files are matched by extension first, then mime prefix.
export function entryKind(entry) {
  if (entry.isDir)
    return { Icon: FolderIcon, color: FOLDER_COLOR, label: 'Folder' };

  const extension = (entry.name.split('.').pop() || '').toLowerCase();
  const byExtension = EXTENSION_KINDS.find((kind) =>
    kind.extensions.includes(extension),
  );
  if (byExtension) return byExtension;

  const mimePrefix = (entry.mimeType || '').split('/')[0];
  if (MIME_KINDS[mimePrefix]) return MIME_KINDS[mimePrefix];

  return { Icon: InsertDriveFileIcon, color: '#70738a', label: 'File' };
}

// Merges a listing response into rows the views render: folders first, each
// group keeps the order the server (or the kind sorter) produced.
export function listingToEntries(listing) {
  if (!listing) return [];
  const directories = (listing.directories || []).map((dir) => ({
    ...dir,
    isDir: true,
  }));
  const files = (listing.files || []).map((file) => ({
    ...file,
    isDir: false,
  }));
  return [...directories, ...files];
}

export function sortEntriesByKind(entries, sortOrder) {
  const direction = sortOrder === 'desc' ? -1 : 1;
  return [...entries].sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return direction * entryKind(a).label.localeCompare(entryKind(b).label);
  });
}
